/**
 * Вид «Что изменилось» вкладки «Голоса клиента» (#2394 §9 п. 7, спека #847
 * §6.2, docs/plans/2026-10-19-voc-trends-ui.md §4): строки всех блоков
 * вкладки с пометкой Р3 — по значимости, с названием блока. Строка — та же
 * карточка: спарклайн, раскрытие до ряда и разговоров корзины.
 *
 * Пометку ставит сервер (`mark`, п. 6) — своего правила здесь нет. Новых
 * запросов вид не делает, кроме рядов отмеченных строк. Пометок нет —
 * словами, почему: правило коротко, а при фильтре темы с несравнимым прошлым
 * окном — что сравнения нет. Упавший блок — сбой с повтором над строками
 * готовых, а не весь вид; блок в загрузке — загрузка под строками готовых.
 */
import { useTranslation } from 'react-i18next';
import { DashboardPanel } from '@/components/dashboard/dashboard-panel';
import { VoiceLoadError } from './VoiceLoadError';
import { VoiceLoading } from './VoiceLoading';
import { VoiceRowCard } from './VoiceRowCard';
import { VoiceSeriesNote } from './VoiceSeriesNote';
import { useVoiceBlockSeries } from './useVoiceSeries';
import { useVoiceComparable } from './voice-theme-filter-context';
import {
  changeRows,
  rowSeriesRef,
  type VoiceChangeRow,
  type VoiceChangesBlock,
} from './voice-series-view-model';
import type { VoiceSectionId } from './voice-search';
import type { VoiceSection } from './voice-view-model';

interface VoiceChangesSectionProps {
  /** The tab's blocks whose rows carry the server's mark (`changesBlock`). */
  blocks: readonly VoiceChangesBlock[];
  expandedRow: string | undefined;
  onToggleRow: (rowId: string) => void;
  onOpenCall: (recordId: string) => void;
  /** Lines under the rows: what a block's numbers mean (Sales — the objections' N). */
  notes?: readonly string[];
  /**
   * The tab's section that holds a block's rows: «Show this month» opens it —
   * the month's marks may not mark the row, and «What changed» would drop it.
   * `undefined` — the tab's first section (the address omits it).
   */
  sourceSection: (block: VoiceSection['id']) => VoiceSectionId | undefined;
}

export function VoiceChangesSection({
  blocks,
  expandedRow,
  onToggleRow,
  onOpenCall,
  notes = [],
  sourceSection,
}: VoiceChangesSectionProps) {
  const { t } = useTranslation('dashboard');
  const comparable = useVoiceComparable();
  const pending = blocks.some((block) => block.status === 'pending');
  const failed = blocks.flatMap((block) => (block.status === 'failed' ? [block] : []));
  const rows = comparable
    ? changeRows(
        blocks.flatMap((block) => (block.status === 'ready' ? block.sections : []))
      )
    : [];
  // «A lower bound» only where a capped scan counted the rows shown: an exact
  // block's marks next to a cut one without marks stay exact (#2596).
  const cutSections = new Set(
    blocks.flatMap((block) =>
      block.status === 'ready' && block.lowerBound
        ? block.sections.map((section) => section.id)
        : []
    )
  );
  // A capped scan's window is never marked by the server: a cut section with
  // no row here is not «no changes», it is a section that cannot say.
  const shownSections = new Set(rows.map((change) => change.section));
  const cutUnmarked = [...cutSections].filter((id) => !shownSections.has(id));
  const cutUnmarkedLine =
    cutUnmarked.length > 0
      ? t('voicePage.changes.cutUnmarked', {
          blocks: cutUnmarked.map((id) => t(`voicePage.sections.${id}`)).join(', '),
        })
      : null;
  const cutShown = [
    ...new Set(
      rows.flatMap((change) => (cutSections.has(change.section) ? [change.section] : []))
    ),
  ];
  // Rows of an exact block among them: the line names the cut blocks.
  const mixed = rows.some((change) => !cutSections.has(change.section));
  const series = useVoiceBlockSeries(rows, ({ row }: VoiceChangeRow) => ({
    ref: rowSeriesRef(row),
    calls: row.calls,
    prevCalls: row.prevCalls,
  }));
  const trendOf = (change: VoiceChangeRow) => {
    const trend = series.trendOf(change);
    return (
      trend && { ...trend, monthPatch: { voiceSection: sourceSection(change.section) } }
    );
  };

  return (
    <div data-testid="voice-changes">
      <DashboardPanel title={t('voicePage.layers.section.changes')}>
        <p className="mb-3 text-xs text-fg-muted">{t('voicePage.changes.hint')}</p>
        {!comparable ? (
          <p className="py-6 text-center text-sm text-fg-muted">
            {t('voicePage.changes.notComparable')}
          </p>
        ) : (
          <>
            {failed.length > 0 && (
              <VoiceLoadError
                onRetry={() => {
                  for (const block of failed) block.retry();
                }}
              />
            )}
            {rows.length > 0 ? (
              <>
                <VoiceSeriesNote
                  block={series.block}
                  lowerBound={cutShown.length > 0}
                  lowerBoundBlocks={
                    mixed
                      ? cutShown.map((section) => t(`voicePage.sections.${section}`))
                      : undefined
                  }
                />
                <div className="space-y-2">
                  {rows.map((change) => (
                    <VoiceRowCard
                      key={change.row.id}
                      row={change.row}
                      expanded={expandedRow === change.row.id}
                      onToggle={() => onToggleRow(change.row.id)}
                      onOpenCall={onOpenCall}
                      blockLabel={t(`voicePage.sections.${change.section}`)}
                      trend={trendOf(change)}
                    />
                  ))}
                </div>
                {cutUnmarkedLine && (
                  <p className="mt-3 text-xs text-fg-muted">{cutUnmarkedLine}</p>
                )}
              </>
            ) : (
              // A failed or loading block may hold the marks: «no changes»
              // would claim it.
              <>
                {cutUnmarkedLine && (
                  <p className="py-6 text-center text-sm text-fg-muted">
                    {cutUnmarkedLine}
                  </p>
                )}
                {!cutUnmarkedLine && failed.length === 0 && !pending && (
                  <p className="py-6 text-center text-sm text-fg-muted">
                    {t('voicePage.changes.empty')}
                  </p>
                )}
              </>
            )}
            {pending && <VoiceLoading />}
          </>
        )}
        <div className="mt-3 space-y-1 text-xs text-fg-muted">
          <p>{t('voicePage.changes.rule')}</p>
          {notes.map((note) => (
            <p key={note}>{note}</p>
          ))}
        </div>
      </DashboardPanel>
    </div>
  );
}
